import { ADMIN_PRINCIPAL_ID } from '@lucidlink/dto/validation';
import { assertCanHandle } from '@lucidlink/tscore';
import { AuthorizationError } from '@lucidlink/web-fs/common';
import { ForbiddenError } from '#fetcher/fetch-error.types';
import { PrincipalNotFoundError } from '#sdk/workspace-client/crypto/workspace-crypto.error';
import { getMapsWithFallback, mapAccessItemsWithPrincipalInfo, mapFsEntriesAccess, remapAccessInfo, remapSinglePrincipalId, } from './utils';
export const createFilePermissionsManager = (permissionsManager, membersMap, groupsMap, serviceAccountsMap, principalsCrypto) => {
    return new FilePermissionsManagerImpl(permissionsManager, membersMap, groupsMap, serviceAccountsMap, principalsCrypto);
};
export const DEFAULT_PAGE_LIMIT = 50;
class FilePermissionsManagerImpl {
    permissionsManager;
    membersMap;
    groupsMap;
    serviceAccountsMap;
    principalsCrypto;
    constructor(permissionsManager, membersMap, groupsMap, serviceAccountsMap, principalsCrypto) {
        this.permissionsManager = permissionsManager;
        this.membersMap = membersMap;
        this.groupsMap = groupsMap;
        this.serviceAccountsMap = serviceAccountsMap;
        this.principalsCrypto = principalsCrypto;
    }
    async grantAccess(principalId, fsEntry, permissions) {
        await this.preloadData();
        return this.permissionsManager.grantAccess(principalId, fsEntry, permissions);
    }
    revokeAccess(principalId, fsEntry) {
        return this.permissionsManager.revokeAccess(principalId, fsEntry);
    }
    async *listAccess(filter) {
        await this.preloadData();
        const accessList = this.permissionsManager.listAccess(filter);
        const { membersMap, groupsMap, serviceAccountsMap } = await getMapsWithFallback(this.membersMap, this.groupsMap, this.serviceAccountsMap);
        for await (const entry of accessList) {
            const result = remapAccessInfo(entry, membersMap, groupsMap, serviceAccountsMap);
            if (result.principalsAccess.length) {
                yield result;
            }
        }
    }
    async listAccessPaginated(params) {
        // Validate that ADMIN_PRINCIPAL_ID is not being queried
        if (params.principalId === ADMIN_PRINCIPAL_ID) {
            throw new PrincipalNotFoundError({
                principalId: params.principalId,
            });
        }
        await this.preloadData();
        const effectiveLimit = params.limit ?? DEFAULT_PAGE_LIMIT;
        const { membersMap, groupsMap, serviceAccountsMap } = await getMapsWithFallback(this.membersMap, this.groupsMap, this.serviceAccountsMap);
        const finalResult = { items: [] };
        let currentResult = {
            items: [],
            nextCursor: params.cursor,
        };
        let numberOfElementsLeft = effectiveLimit;
        do {
            currentResult = await this.permissionsManager.listAccessPaginated({
                principalId: params.principalId,
                limit: numberOfElementsLeft,
                cursor: currentResult.nextCursor,
            });
            // Add principal info to each access object. If a mapping fails,
            // remove the corresponding access object from the list
            const currentItems = mapAccessItemsWithPrincipalInfo(currentResult.items, membersMap, groupsMap, serviceAccountsMap);
            finalResult.items = finalResult.items.concat(currentItems);
            finalResult.nextCursor = currentResult.nextCursor;
            numberOfElementsLeft -= currentItems.length;
        } while (currentResult.nextCursor &&
            finalResult.items.length < effectiveLimit);
        return finalResult;
    }
    async getPrincipalsAccess(principals) {
        await this.preloadData();
        const principalsAccess = await this.permissionsManager.getPrincipalsAccess(principals);
        // https://lucidlink.atlassian.net/browse/LUCID-22861
        const { membersMap, groupsMap, serviceAccountsMap } = await getMapsWithFallback(this.membersMap, this.groupsMap, this.serviceAccountsMap, true);
        const result = {};
        for (const [principalId, access] of principalsAccess) {
            const principal = remapSinglePrincipalId(principalId, membersMap, groupsMap, serviceAccountsMap);
            if (principal) {
                result[principalId] = {
                    principal,
                    fsEntriesAccess: mapFsEntriesAccess(access),
                };
            }
        }
        return result;
    }
    async preloadData(onlyPrincipals = false) {
        try {
            return await this.doPreloadData(onlyPrincipals);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof ForbiddenError);
            throw new AuthorizationError(err.message);
        }
    }
    async doPreloadData(onlyPrincipals) {
        // Resolving the principals crypto asserts the caller has admin access;
        // the principals themselves are fetched on demand.
        await this.principalsCrypto.get();
        if (onlyPrincipals) {
            return;
        }
        this.membersMap.prefetch();
        this.groupsMap.prefetch();
        this.serviceAccountsMap.prefetch();
    }
}
